import moment from "moment";
import { Cell } from "react-table";
import { FacilityUsageSlotSimple } from "src/api/contract/facility-usage-slot-types";
import { BaseTooltip } from "src/components";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import PagePath from "src/pagePath.json";
import { getStatusTextAndColor } from "src/pages/reservation/reservation-types";
import { numberToStringWithComma, ViewYmdFormat } from "src/utils/common-util";
import { getContractDetailPath } from "src/utils/route-util";
import { facilityTypeLabel, facilityUsageStatusLabel } from "../../commonSpaceSettlement-types";
import FacilityUsageStatusChip from "../../components/FacilityUsageStatusChip";

// 일시 포맷 (화면: 분 단위)
const formatDateTime = (value?: string, format: string = ViewYmdFormat.YYYY_MM_DD_HH_MM) => (value ? moment(value).format(format) : "-");
// 일시 포맷 (초 단위: 최종 처리 일시 화면 / 엑셀 추출)
const formatFullDateTime = (value?: string) => formatDateTime(value, ViewYmdFormat.FULL);
// 금액 포맷
const formatAmount = (value?: number) => (value !== undefined && value !== null ? numberToStringWithComma(value) : "-");
// 위치 포맷 (양수 = 지상, 음수 = 지하 / 공용공간 예약 화면과 동일 규칙)
const formatFloor = (floor?: number) => {
  if (floor === undefined || floor === null) return "-";
  return `${floor > 0 ? "지상" : "지하"} ${Math.abs(floor)}층`;
};

/**
 * 공용공간 정산 목록 컬럼 정의 (RCL31 출력 항목 기준)
 * - excelValue: 엑셀 다운로드 시 화면 표시 텍스트와 1:1 매칭 (일시는 초 단위)
 */
export const commonSpaceSettlementColumns: Array<any> = [
  {
    Header: "정산 ID",
    accessor: "usageSlotId",
    sticky: "left",
    width: 120,
    Cell: (props: Cell<FacilityUsageSlotSimple>) => props.value ?? "-",
    excelValue: (item: FacilityUsageSlotSimple) => item.usageSlotId,
  },
  {
    Header: "예약 ID",
    accessor: "reservationId",
    width: 120,
    Cell: (props: Cell<FacilityUsageSlotSimple>) =>
      props.value ? (
        <BaseNewTabLink path={PagePath.reservation.detail.replace(":id", String(props.value))} value={String(props.value)} className="w-100" />
      ) : (
        "-"
      ),
    excelValue: (item: FacilityUsageSlotSimple) => item.reservationId,
  },
  {
    Header: "예약명",
    accessor: "reservationName",
    width: 140,
    Cell: (props: Cell<FacilityUsageSlotSimple>) => <BaseTooltip contents={props.value || "-"} />,
    excelValue: (item: FacilityUsageSlotSimple) => item.reservationName,
  },
  {
    Header: "예약 상태",
    accessor: "reservationStatusCode",
    width: 90,
    align: "center",
    Cell: (props: Cell<FacilityUsageSlotSimple>) => {
      const { reservationStartDateTime, reservationEndDateTime } = props.row.original;
      const status = getStatusTextAndColor(props.value, reservationStartDateTime, reservationEndDateTime);
      return <div className="w-100 flex-center-center"><div className={`${status.color}`}>{status.label || "-"}</div></div>;
    },
    excelValue: (item: FacilityUsageSlotSimple) =>
      getStatusTextAndColor(item.reservationStatusCode, item.reservationStartDateTime, item.reservationEndDateTime).label,
  },
  {
    Header: "계약 ID",
    accessor: "contractId",
    width: 120,
    // 선택 시 해당 계약 상세 페이지로 이동
    Cell: (props: Cell<FacilityUsageSlotSimple>) =>
      props.value ? (
        <BaseNewTabLink path={getContractDetailPath({ contractId: String(props.value) })} value={String(props.value)} className="w-100" />
      ) : (
        "-"
      ),
    excelValue: (item: FacilityUsageSlotSimple) => item.contractId,
  },
  {
    Header: "계약명",
    accessor: "contractName",
    width: 160,
    Cell: (props: Cell<FacilityUsageSlotSimple>) => <BaseTooltip contents={props.value || "-"} />,
    excelValue: (item: FacilityUsageSlotSimple) => item.contractName,
  },
  {
    Header: "소속 회사",
    accessor: "organizationName",
    width: 140,
    Cell: (props: Cell<FacilityUsageSlotSimple>) => <BaseTooltip contents={props.value || "-"} />,
    excelValue: (item: FacilityUsageSlotSimple) => item.organizationName,
  },
  {
    Header: "건물명",
    accessor: "buildingName",
    width: 140,
    Cell: (props: Cell<FacilityUsageSlotSimple>) => <BaseTooltip contents={props.value || "-"} />,
    excelValue: (item: FacilityUsageSlotSimple) => item.buildingName,
  },
  {
    Header: "공용공간 ID",
    accessor: "facilityId",
    width: 120,
    Cell: (props: Cell<FacilityUsageSlotSimple>) => props.value ?? "-",
    excelValue: (item: FacilityUsageSlotSimple) => item.facilityId,
  },
  {
    Header: "공용공간 유형",
    accessor: "facilityType",
    width: 100,
    align: "center",
    Cell: (props: Cell<FacilityUsageSlotSimple>) => (
      <p className="w-100 text-center">{props.value ? facilityTypeLabel[props.value as keyof typeof facilityTypeLabel] || props.value : "-"}</p>
    ),
    excelValue: (item: FacilityUsageSlotSimple) =>
      item.facilityType ? facilityTypeLabel[item.facilityType] || item.facilityType : "",
  },
  {
    Header: "위치",
    accessor: "facilityFloor",
    width: 80,
    Cell: (props: Cell<FacilityUsageSlotSimple>) => formatFloor(props.value),
    excelValue: (item: FacilityUsageSlotSimple) => formatFloor(item.facilityFloor),
  },
  {
    Header: "공용공간명",
    accessor: "facilityName",
    width: 140,
    // 줄바꿈 후 말줄임 처리되는 경우 호버로 전체 명칭 노출
    Cell: (props: Cell<FacilityUsageSlotSimple>) => <BaseTooltip contents={props.value || "-"} />,
    excelValue: (item: FacilityUsageSlotSimple) => item.facilityName,
  },
  {
    Header: "예약 시작일시",
    accessor: "reservationStartDateTime",
    width: 160,
    align: "center",
    Cell: (props: Cell<FacilityUsageSlotSimple>) => formatDateTime(props.value),
    excelValue: (item: FacilityUsageSlotSimple) => formatFullDateTime(item.reservationStartDateTime),
  },
  {
    Header: "예약 종료일시",
    accessor: "reservationEndDateTime",
    width: 160,
    align: "center",
    Cell: (props: Cell<FacilityUsageSlotSimple>) => formatDateTime(props.value),
    excelValue: (item: FacilityUsageSlotSimple) => formatFullDateTime(item.reservationEndDateTime),
  },
  {
    Header: "소진 시작일시",
    accessor: "startDateTime",
    width: 160,
    align: "center",
    Cell: (props: Cell<FacilityUsageSlotSimple>) => formatDateTime(props.value),
    excelValue: (item: FacilityUsageSlotSimple) => formatFullDateTime(item.startDateTime),
  },
  {
    Header: "소진 종료일시",
    accessor: "endDateTime",
    width: 160,
    align: "center",
    Cell: (props: Cell<FacilityUsageSlotSimple>) => formatDateTime(props.value),
    excelValue: (item: FacilityUsageSlotSimple) => formatFullDateTime(item.endDateTime),
  },
  {
    Header: "소진시간(분)",
    accessor: "usageMinutes",
    width: 160,
    align: "right",
    Cell: (props: Cell<FacilityUsageSlotSimple>) => (props.value !== undefined && props.value !== null ? props.value : "-"),
    excelValue: (item: FacilityUsageSlotSimple) => item.usageMinutes,
  },
  {
    Header: "공용공간 요금(원)",
    accessor: "salesAmount",
    width: 160,
    Cell: (props: Cell<FacilityUsageSlotSimple>) => <p className="w-100 text-right">{formatAmount(props.value)}</p>,
    excelValue: (item: FacilityUsageSlotSimple) => formatAmount(item.salesAmount),
  },
  {
    Header: "사용 충전금(원)",
    accessor: "usedCredit",
    width: 160,
    Cell: (props: Cell<FacilityUsageSlotSimple>) => <p className="w-100 text-right">{formatAmount(props.value)}</p>,
    excelValue: (item: FacilityUsageSlotSimple) => formatAmount(item.usedCredit),
  },
  {
    Header: "청구 대상 요금(원)",
    accessor: "billAmount",
    width: 160,
    Cell: (props: Cell<FacilityUsageSlotSimple>) => <p className="w-100 text-right">{formatAmount(props.value)}</p>,
    excelValue: (item: FacilityUsageSlotSimple) => formatAmount(item.billAmount),
  },
  {
    Header: "정산 상태",
    accessor: "usageStatus",
    width: 90,
    align: "center",
    Cell: (props: Cell<FacilityUsageSlotSimple>) => <div className="w-100 flex-center-center"><FacilityUsageStatusChip status={props.value} /></div>,
    excelValue: (item: FacilityUsageSlotSimple) => (item.usageStatus ? facilityUsageStatusLabel[item.usageStatus] : ""),
  },
  {
    Header: "최종 처리 일시",
    accessor: "modifiedAt",
    width: 170,
    align: "center",
    Cell: (props: Cell<FacilityUsageSlotSimple>) => formatFullDateTime(props.value),
    excelValue: (item: FacilityUsageSlotSimple) => formatFullDateTime(item.modifiedAt),
  },
];

// 정렬(Sorting) 가능 컬럼 (RCL31 정렬 속성)
const sortableAccessors = [
  "usageSlotId",
  "reservationStartDateTime",
  "reservationEndDateTime",
  "startDateTime",
  "endDateTime",
  "usageMinutes",
  "salesAmount",
  "usedCredit",
  "billAmount",
  "modifiedAt",
];

// 정렬 비활성화 컬럼 (BaseTable disabledSortHeaders)
export const commonSpaceSettlementDisabledSortHeaders: string[] = commonSpaceSettlementColumns
  .map((column) => column.accessor)
  .filter((accessor) => !sortableAccessors.includes(accessor));
